/**
 * Backend API error codes (UPPER_SNAKE_CASE strings).
 * Use with `getApiErrorCode()` / `isApiError()` — responses may be a plain string or `{ name, error, code, why }`.
 */
export enum ApiErrorEnum {
  Denied = 'DENIED',
  // Auth & session
  SessionExpired = 'SESSION_EXPIRED',
  UserBlocked = 'USER_BLOCKED',
  UserExpired = 'USER_EXPIRED',
  TenantNotFound = 'TENANT_NOT_FOUND',
  IpRegionBlocked = 'IP_REGION_BLOCKED',
  IpRegionAdminLockout = 'IP_REGION_ADMIN_LOCKOUT',
  InvalidToken = 'INVALID_TOKEN',
  OtpWrong = 'OTP_WRONG',
  /** otp/v2: value is neither a valid email nor a valid login alias format. */
  EmailFormatNotCorrect = 'EMAILFORMATNOTCORRECT',
  TotpSetupRequired = 'TOTP_SETUP_REQUIRED',
  TotpCodeRequired = 'TOTP_CODE_REQUIRED',
  TotpVerificationFailed = 'TOTP_VERIFICATION_FAILED',
  AccountDisabled = 'ACCOUNT_DISABLED',
  AccountExpired = 'ACCOUNT_EXPIRED',
  Forbidden = 'FORBIDDEN',
  Unauthorized = 'UNAUTHORIZED',
  LockedOut = 'LOCKED_OUT',

  // License
  LicenseExpired = 'LICENSE_EXPIRED',
  LicenseGlobalExpired = 'LICENSE_GLOBAL_EXPIRED',
  LicenseCompanyExpired = 'LICENSE_COMPANY_EXPIRED',
  LicenseMaxUsersReached = 'LICENSE_MAX_USERS_REACHED',
  LicenseMaxTotalUsersReached = 'LICENSE_MAX_TOTAL_USERS_REACHED',
  LicenseInvalidMaxUsers = 'LICENSE_INVALID_MAX_USERS',

  // Users & groups
  NameAlreadyUsed = 'NAME_ALREADY_USED',
  UserReferencedInSettings = 'USER_REFERENCED_IN_SETTINGS',
  /** loginAlias has forbidden characters or length > 256. */
  LoginAliasNotValid = 'LOGIN_ALIAS_NOT_VALID',
  /** loginAlias (or email on create) collides with another user's alias or email. */
  LoginAliasAlreadyUsed = 'LOGIN_ALIAS_ALREADY_USED',

  // File access, sharing & download
  AntiCaptureActivated = 'ANTICAPTURE_ACTIVATED',
  DownloadRestrictionActivated = 'DOWNLOAD_RESTRICTION_ACTIVATED',
  WatermarkProtectionDownloadDenied = 'WATERMARK_PROTECTION_DOWNLOAD_DENIED',
  ObjectNotFound = 'OBJECT_NOT_FOUND',
  PasswordRequired = 'PASSWORD_REQUIRED',
  FolderPasswordRequired = 'FOLDER_PASSWORD_REQUIRED',
  FolderPasswordLockout = 'FOLDER_PASSWORD_LOCKOUT',
  FileSizeViewerOver = 'FILE_SIZE_VIEWER_OVER',
  EditSharingDenied = 'EDIT_SHARING_DENIED',
  ExternalSharingNotAllowed = 'EXTERNAL_SHARING_NOT_ALLOWED',
  SharingNotAllowedMandatoryTag = 'SHARING_NOT_ALLOWED_MANDATORY_TAG',
  FileSharingDenied = 'FILE_SHARING_DENIED',
  CannotAccessFiles = 'CANNOT_ACCESS_FILES',
  PartialSharingDenied = 'PARTIAL_SHARING_DENIED',

  // Upload
  FileTypeRestriction = 'FILE_TYPE_RESTRICTION',
  FileAlreadyInUse = 'FILE_ALREADY_IN_USE',
  FileSizeLimitMessage = 'FILE_SIZE_LIMIT_MESSAGE',
  ContentHashMismatch = 'CONTENT_HASH_MISMATCH',

  // AI classification
  FilesAlreadyClassified = 'FILES_ALREADY_CLASSIFIED',
  FilesBeingClassified = 'FILES_BEING_CLASSIFIED',
  FilesBeingClassifiedByOthers = 'FILES_BEING_CLASSIFIED_BY_OTHERS',
  FilesBeingClassifiedByMe = 'FILES_BEING_CLASSIFIED_BY_ME',
  FilesTooBig = 'FILES_TOO_BIG',
  TooManyFiles = 'TOO_FILES',
  BatchNotFound = 'BATCH_NOT_FOUND',
  BatchNotCancellable = 'BATCH_NOT_CANCELLABLE',
  BatchRetryNotSupported = 'BATCH_RETRY_NOT_SUPPORTED',
  NoFileJobsFound = 'NO_FILE_JOBS_FOUND',
  QueueUnavailable = 'QUEUE_UNAVAILABLE',

  // Public link (guest access)
  LinkNotFound = 'LINK_NOT_FOUND',
  LinkRevoked = 'LINK_REVOKED',
  LinkExpired = 'LINK_EXPIRED',
  MaxAccessReached = 'MAX_ACCESS_REACHED',
  InvalidAccessCode = 'INVALID_ACCESS_CODE',

  // Public link creation
  PublicLinkSettingDisabled = 'PUBLIC_LINK_SETTING_DISABLED',
  PublicLinkClassificationRequired = 'PUBLIC_LINK_CLASSIFICATION_REQUIRED',
  PublicLinkPasswordRequired = 'PUBLIC_LINK_PASSWORD_REQUIRED',
  PublicLinkMaxAccessCountRequired = 'PUBLIC_LINK_MAX_ACCESS_COUNT_REQUIRED',
  PublicLinkExpirationRequired = 'PUBLIC_LINK_EXPIRATION_REQUIRED',
  PublicLinkExpirationExceeded = 'PUBLIC_LINK_EXPIRATION_EXCEEDED',
  PublicLinkAlreadyExists = 'PUBLIC_LINK_ALREADY_EXISTS',
  PublicLinkExpirationInPast = 'PUBLIC_LINK_EXPIRATION_IN_PAST',
  PublicLinkFolderPasswordProtected = 'PUBLIC_LINK_FOLDER_PASSWORD_PROTECTED',
  PublicLinkDownloadRestricted = 'PUBLIC_LINK_DOWNLOAD_RESTRICTED',
  PublicLinkAnticaptureActive = 'PUBLIC_LINK_ANTICAPTURE_ACTIVE',
  PublicLinkExternalSharingRestricted = 'PUBLIC_LINK_EXTERNAL_SHARING_RESTRICTED',
  PublicLinkResharingDenied = 'PUBLIC_LINK_RESHARING_DENIED',
  PublicLinkDownloadTagsRestricted = 'PUBLIC_LINK_DOWNLOAD_TAGS_RESTRICTED',
  PublicLinkApprovalRequired = 'PUBLIC_LINK_APPROVAL_REQUIRED',
  PublicLinkMandatoryExpiration = 'PUBLIC_LINK_MANDATORY_EXPIRATION',

  // Login tokens
  CannotCreateTokenForSuperAdmin = 'CANNOT_CREATE_TOKEN_FOR_SUPERADMIN',
  MaxLoginTokensReached = 'MAX_LOGIN_TOKENS_REACHED',
  TokenAlreadyUsed = 'TOKEN_ALREADY_USED',

  // TOTP & passkey
  AdminTotpResetNotAllowed = 'ADMIN_TOTP_RESET_NOT_ALLOWED',
  TotpNotEnabled = 'TOTP_NOT_ENABLED',
  LowBackupCodes = 'LOW_BACKUP_CODES',
  PasskeyNotEnabled = 'PASSKEY_NOT_ENABLED',
  PasskeyNotAllowed = 'PASSKEY_NOT_ALLOWED',
  PasskeyDataRequired = 'PASSKEY_DATA_REQUIRED',
  PasskeyVerificationFailed = 'PASSKEY_VERIFICATION_FAILED',
  PasskeyLimitReached = 'PASSKEY_LIMIT_REACHED',
  PasskeyNotFound = 'PASSKEY_NOT_FOUND',
  PasskeyLockedOut = 'PASSKEY_LOCKED_OUT',
  // Server-side WebAuthn challenge gone (TTL expired, already consumed, or another API instance):
  // the ceremony must be restarted from the options call.
  PasskeySessionExpired = 'PASSKEY_SESSION_EXPIRED',

  MandatoryClassificationCannotRemove = 'MANDATORY_CLASSIFICATION_CANNOT_REMOVE',
  TagRemovalDenied = 'TAG_REMOVAL_DENIED',
  TagAdditionDenied = 'TAG_ADDITION_DENIED',

  // Folder password
  FolderPasswordPasswordRequired = 'FOLDER_PASSWORD_PASSWORD_REQUIRED',
  FolderPasswordTooShort = 'FOLDER_PASSWORD_TOO_SHORT',
  FolderPasswordNotAFolder = 'FOLDER_PASSWORD_NOT_A_FOLDER',
  FolderPasswordOwnerOnly = 'FOLDER_PASSWORD_OWNER_ONLY',
  FolderPasswordAlreadyExists = 'FOLDER_PASSWORD_ALREADY_EXISTS',
  FolderPasswordAncestorProtected = 'FOLDER_PASSWORD_ANCESTOR_PROTECTED',
  FolderPasswordDescendantProtected = 'FOLDER_PASSWORD_DESCENDANT_PROTECTED',
  FolderPasswordExpirationInPast = 'FOLDER_PASSWORD_EXPIRATION_IN_PAST',
  FolderPasswordNotSet = 'FOLDER_PASSWORD_NOT_SET',
  FolderPasswordNotProtected = 'FOLDER_PASSWORD_NOT_PROTECTED',
  FolderPasswordCurrentRequired = 'FOLDER_PASSWORD_CURRENT_REQUIRED',
  FolderPasswordExpired = 'FOLDER_PASSWORD_EXPIRED',
  FolderPasswordIncorrect = 'FOLDER_PASSWORD_INCORRECT',
  FolderPasswordSessionNotFound = 'FOLDER_PASSWORD_SESSION_NOT_FOUND',
  FolderPasswordSessionForbidden = 'FOLDER_PASSWORD_SESSION_FORBIDDEN',
  FolderPasswordOtpNotRequested = 'FOLDER_PASSWORD_OTP_NOT_REQUESTED',
  FolderPasswordOtpRateLimited = 'FOLDER_PASSWORD_OTP_RATE_LIMITED',
  FolderPasswordOtpLockedOut = 'FOLDER_PASSWORD_OTP_LOCKED_OUT',
  FolderPasswordOtpExpired = 'FOLDER_PASSWORD_OTP_EXPIRED',

  // Single Sign-On — Microsoft Entra ID client certificate
  /** Uploaded PKCS#12 could not be opened (bad base64, wrong password, expired, no RSA key, …). */
  SsoCertificateInvalid = 'SSO_CERTIFICATE_INVALID',
  /** A non-empty certificate was sent on a Google (secret-only) provider entry. */
  SsoCertificateNotSupported = 'SSO_CERTIFICATE_NOT_SUPPORTED',

  // SharePoint integration (SharePointGrant)
  SharePointNotAvailable = 'SHAREPOINT_NOT_AVAILABLE',
  SharePointNotConfigured = 'SHAREPOINT_NOT_CONFIGURED',
  /**
   * No `connection.spaClientId` in the setting: there is no public app registration for the browser to
   * sign into, so no delegated Graph token can be obtained and no encrypted file can be opened. Only
   * an administrator can fix it, in the SharePoint integration setting.
   */
  SharePointSpaAppNotConfigured = 'SHAREPOINT_SPA_APP_NOT_CONFIGURED',
  SharePointConfigInvalid = 'SHAREPOINT_CONFIG_INVALID',
  SharePointGraphUnauthorized = 'SHAREPOINT_GRAPH_UNAUTHORIZED',
  SharePointGraphUnavailable = 'SHAREPOINT_GRAPH_UNAVAILABLE',
  SharePointSiteNotFound = 'SHAREPOINT_SITE_NOT_FOUND',
  /** The selected certificate id is not in the company's RemoteGrant certificate list. */
  SharePointCertificateNotFound = 'SHAREPOINT_CERTIFICATE_NOT_FOUND',

  // RemoteGrant — the certificate authority behind the SharePoint encryption keys
  /** The company has no RemoteGrant keys: only CyberGrant can set them, from the backoffice. */
  RemoteGrantNotConfigured = 'REMOTE_GRANT_NOT_CONFIGURED',
  /** RemoteGrant is unreachable, timed out, or answered with something unparseable. */
  RemoteGrantUnavailable = 'REMOTE_GRANT_UNAVAILABLE',
  /** RemoteGrant rejected the company's credentials. */
  RemoteGrantDenied = 'REMOTE_GRANT_DENIED',

  // Antivirus
  AntivirusFileInfected = 'ANTIVIRUS_FILE_INFECTED',
  AntivirusNotAvailable = 'ANTIVIRUS_NOT_AVAILABLE',
  AntivirusRescanNotAllowed = 'ANTIVIRUS_RESCAN_NOT_ALLOWED',

  // PDF Conversion
  PdfConversionUnsupportedFormat = 'PDF_CONVERSION_UNSUPPORTED_FORMAT',
  PdfConversionFileTooLarge = 'PDF_CONVERSION_FILE_TOO_LARGE',
  PdfConversionFailed = 'PDF_CONVERSION_FAILED',
  PdfConversionAntivirusBlocked = 'PDF_CONVERSION_ANTIVIRUS_BLOCKED',
  FileNotFound = 'FILE_NOT_FOUND',

  // Active Directory / LDAP sync
  ActiveDirectoryNotAvailable = 'ACTIVE_DIRECTORY_NOT_AVAILABLE',
  LdapNotConfigured = 'LDAP_NOT_CONFIGURED',
  LdapConfigurationInvalid = 'LDAP_CONFIGURATION_INVALID',
  LdapCredentialRequired = 'LDAP_CREDENTIAL_REQUIRED',
  LdapUnreachable = 'LDAP_UNREACHABLE',
  LdapTlsFailed = 'LDAP_TLS_FAILED',
  LdapBindFailed = 'LDAP_BIND_FAILED',
  LdapBaseDnNotFound = 'LDAP_BASE_DN_NOT_FOUND',
  LdapSearchFailed = 'LDAP_SEARCH_FAILED',
  LdapCredentialEncryptionFailed = 'LDAP_CREDENTIAL_ENCRYPTION_FAILED',
  DirectorySyncAlreadyRunning = 'DIRECTORY_SYNC_ALREADY_RUNNING',

  // Custom outbound e-mail domain (OUTBOUND_EMAIL)
  OutboundEmailNotAvailable = 'OUTBOUND_EMAIL_NOT_AVAILABLE',
  OutboundEmailConfigInvalid = 'OUTBOUND_EMAIL_CONFIG_INVALID',
  OutboundEmailNotConfigured = 'OUTBOUND_EMAIL_NOT_CONFIGURED',
  OutboundEmailNotReady = 'OUTBOUND_EMAIL_NOT_READY',
  OutboundEmailConcurrentChange = 'OUTBOUND_EMAIL_CONCURRENT_CHANGE',
  OutboundEmailDnsLookupFailed = 'OUTBOUND_EMAIL_DNS_LOOKUP_FAILED',
  OutboundEmailMailgunFailed = 'OUTBOUND_EMAIL_MAILGUN_FAILED',
  OutboundEmailTestFailed = 'OUTBOUND_EMAIL_TEST_FAILED',

  // Generic parameter validation
  InvalidParams = 'INVALID_PARAMS',
}
